import React, { Suspense } from "react";
import { FileIcon, defaultStyles } from "react-file-icon";

import { allowedImageTypes, heicFileFormats } from "@/app/constants/constants";
import InvalidImg from "@/assets/images/invalid-img.png";
import { getExtension } from "@/utils/content/fileImage.utils";
import { preventImagePreviewContextMenu } from "@/utils/content/imageHelper";
import { cn } from "@/utils/core/generalUtils";

const ImagePreviewer = React.lazy(() =>
  import("@/components/imagePreview/ImagePreviewer").then((module) => ({
    default: module.ImagePreviewer,
  })),
);

type FileImagePreviewProps = {
  file: File | string;
  className?: string;
  config?: {
    onClick?: () => void;
  };
  extension?: string;
  size?: number;
};

const handlePreviewImageError = (
  event: React.SyntheticEvent<HTMLImageElement>,
): void => {
  if (event.currentTarget.dataset.fallbackApplied === "true") {
    return;
  }

  event.currentTarget.dataset.fallbackApplied = "true";
  event.currentTarget.src = InvalidImg;
};

const getImageSrc = (fileExt: string, src: string): string => {
  return heicFileFormats?.includes(fileExt) ? "/heic-preview.png" : src;
};

/**
 * Renders an image preview for supported image files and a file icon for other
 * file types.
 *
 * @param {FileImagePreviewProps} props The preview configuration.
 * @returns {React.JSX.Element | null} The rendered preview element.
 */
export function FileImagePreview({
  file,
  config,
  className,
  extension,
  size,
}: FileImagePreviewProps): React.JSX.Element | null {
  const isFileObject = typeof file !== "string";
  const filename = isFileObject ? file.name : file;
  const fileExt = extension ?? getExtension(filename);
  const src = isFileObject ? URL.createObjectURL(file) : file;
  const isImage = allowedImageTypes.includes(fileExt);

  if (isImage) {
    const trigger = (
      <img
        src={getImageSrc(fileExt, src)}
        width={size || 40}
        height={size || 40}
        alt={filename}
        className={cn("object-cover rounded-lg", className)}
        onError={handlePreviewImageError}
        onContextMenu={preventImagePreviewContextMenu}
      />
    );

    return (
      <Suspense fallback={trigger}>
        <div onClick={config?.onClick}>
          <ImagePreviewer imagesSrc={src} alt={src} trigger={trigger} />
        </div>
      </Suspense>
    );
  }

  return (
    <button
      type='button'
      onClick={config?.onClick}
      className={cn("w-5 h-5", className)}
      aria-label={`Preview ${filename}`}
    >
      <FileIcon
        extension={fileExt}
        // @ts-expect-error - defaultStyles[fileExt] might not be typed correctly
        {...defaultStyles[fileExt]}
      />
    </button>
  );
}
